<i18n>
{
  "de": {
    "login": "Anmelden",
    "followedArtists": "Deine Künstler",
    "hi": "Hi, ",
    "logOut": "Abmelden",
    "followHere": "Folge {artist} hier!"
  },
  "en": {
    "login": "Login",
    "followedArtists": "Your artists",
    "hi": "Hi, ",
    "logOut": "Log out",
    "followHere": "Follow {artist} here!"
  }
}
</i18n>

<template>
  <nav
    id="mainnav"
    ref="mainnav"
    class="navbar is-transparent"
    :class="{ mobile: mobileMenuOpen }"
  >
    <div class="container">
      <div class="navbar-menu">
        <div class="navbar-start">
          <a
            v-if="
              $route &&
                $route.name && !isProfileRoute"
            href="/"
            class="nav-item nav-brand"
          >
            <div class="logo-wrapper">
              <img src="/img/logo_getnext_25_11_18_3.svg">
            </div>
          </a>
          <nuxt-link
            v-else-if="hasBrandingUrl"
            :to="localePath({
              name: 'id-posts',
              params: { id: artist.urlAlias }
            })"
            class="nav-item nav-brand"
          >
            <div class="brand-wrapper">
              <img
                :src="brandingSrc"
                :srcset="brandingSrcSet"
                :alt="`${artist.nickname} Logo`"
              >
            </div>
          </nuxt-link>
        </div>
        <div class="navbar-item is-expanded is-paddingless ml-20 artist-search-container">
          <artist-search
            refs="artistSearch"
            name="desktopSearch"
            class="artist-search"
            :searchMaximized="searchMaximized"
            @blur="exitSearch"
          />
        </div>
        <!-- <div
          v-if="isProfileRoute"
          class="navbar-item is-expanded is-paddingless"
        >
          <transition name="fade">
            <div
              v-if="artist && !artist.followed && navbarOpaque && isMobileView"
              class="navbar-follow-container"
            >
              <followBanner :navbar-mode="true" />
            </div>
          </transition>
        </div> -->

        <div class="navbar-end">
          <user-menu />
        </div>
      </div>
    </div>
    <mobileNav
      ref="mobileNav"
      :class="{ open: mobileMenuOpen }"
      @logoperation="handleLogOperation"
    />
    <div
      :class="{ 'dropdown-backdrop': followBoxOpened }"
      @click.capture="dropdownToggle"
    />
    <div
      :class="{ 'search-backdrop': searchMaximized && !mobileMenuOpen }"
      @click.capture="exitSearch"
    />
    <portal :to="followshipTarget">
      <followBox />
    </portal>
    <section
      v-if="!isAuthenticated && showLogin"
      class="section"
    >
      <modal
        :backdropClose="!socialRegistration"
        defaultHeader="true"
        :defaultFooter="!socialRegistration"
        @close="closeLoginModal"
      >
        <div slot="header">
          {{ modalLoginTitle }}
        </div>
        <div slot="body">
          <div class="content">
            <div
              class="
                modal-card
                animated
                slideInDown
                login-modal
                pt-10
                pl-10
                pr-10
              "
            >
              <socialLogin
                :params="{ isWizard: true }"
                :divider="true"
                @registration="setSocialRegistration"
                @logged="closeLoginModal"
              />
              <div v-if="!socialRegistration">
                <login
                  :params="{ isWizard: true }"
                  @viewChanged="setModalLoginTitle"
                  @logged="closeLoginModal"
                />
              </div>
            </div>
          </div>
        </div>
      </modal>
    </section>
  </nav>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import artistSearch from '~/components/artist-search'
import mobileNav from '~/components/mobilenav'
import followBox from '~/components/follow-box'
import login from '~/components/login'
import socialLogin from '~/components/social-login'
import modal from '~/components/modal'
import userMenu from '~/components/user-menu'

export default {
  components: { artistSearch, mobileNav, followBox, modal, login, socialLogin, userMenu },

  provide() {
    return {
      exitSearch: this.exitSearch,
      focusedSearch: this.focusedSearch
    }
  },

  data: () => ({
    mobileLogout: false,
    followBoxOpened: false,
    showLogin: false,
    modalLoginTitle: '',
    socialRegistration: false,
    scrollTo: false,
    searchMaximized: false,
    navbarOpaque: false
  }),

  computed: {
    followshipTarget() {
      return this.mobileMenuOpen ? 'mobileFollowship' : 'desktopFollowship'
    },
    hasBrandingUrl() {
      return this.artist &&
        this.artist.profile &&
        this.artist.profile.imgs &&
        this.artist.profile.imgs.branding &&
        this.artist.profile.imgs.branding.name
    },
    brandingSrc() {
      return `/services/media/image/${this.artist._id}/${this.artist.profile.imgs.branding.name}/0/40/image.webp`
    },
    brandingSrcSet() {
      return `/services/media/image/${this.artist._id}/${this.artist.profile.imgs.branding.name}/0/80/image.webp 2x, /services/media/image/${this.artist._id}/${this.artist.profile.imgs.branding.name}/0/120/image.webp 3x`
    },
    isProfileRoute() {
      return this.$route && this.$route.name && (this.$route.name.startsWith('id__') || this.$route.name.startsWith('id-'))
    },
    ...mapState(['artist', 'isAuthenticated', 'mobileMenuOpen'])
  },

  watch: {
    mobileLogout() {
      this.toggleMobileMenu()
    }
  },

  methods: {
    async doLogout(options) {
      const data = await this.apiGet('/api/private/logout')
      if (data) {
        this.showLogin = false
        if (!options || !options.localStorageSignal) {
          window.localStorage.setItem('logoutTime', `${Date.now()}`)
        }
        this.logout()
        this.$root.$emit('refreshAPI')
      }
    },

    async handleLogOperation(type) {
      if (type === 'logout') {
        await this.doLogout()
      }
      this.toggleMobileMenu()
    },

    exitSearch() {
      if (this.mobileMenuOpen) {
        this.toggleMobileMenu()
        return
      }
      this.searchMaximized = false
    },

    scrollHandler() {
      const mainnav = this.$refs.mainnav
      const offset = window.scrollY
      if (offset <= 0) mainnav.classList.remove('light-shaded')
      if (offset > 0) mainnav.classList.add('light-shaded')
    },

    widthHandler(e) {
      if (e.currentTarget.innerWidth > 1023 && this.mobileMenuOpen) {
        this.toggleMobileMenu()
      }
    },

    toggleMobileMenu() {
      this.$store.dispatch('toggleMobileMenuOpen')
      document.body.classList.toggle('no-scroll')
    },

    dropdownToggle() {
      this.followBoxOpened = !this.followBoxOpened
      const dropdown = this.$refs.dropdown
      dropdown.classList.toggle('is-active')
    },

    showModalLogin(options) {
      if (options && options.scrollTo) {
        this.scrollTo = options.scrollTo
      }
      this.storeReturnRouteObject({
        route: this.$route,
        locales: this.$root.$i18n.locales,
        locale: this.$i18n.locale
      })
      this.$root.$emit('toggleBodyScrollable')
      this.showLogin = true
    },

    closeLoginModal() {
      this.$root.$emit('toggleBodyScrollable')
      this.showLogin = false
      if (this.scrollTo) {
        this.$scrollTo(this.scrollTo)
      }
    },

    setSocialRegistration(value) {
      this.socialRegistration = value
    },

    setModalLoginTitle(title) {
      this.modalLoginTitle = title
    },

    storageEvents(e) {
      if (!e || e.storageArea !== localStorage) {
        return
      }
      if (e.key === 'logoutTime') {
        return this.doLogout({ localStorageSignal: true })
      }
    },

    focusedSearch(val) {
      this.searchMaximized = val
    },

    handleNavbarOpacity(isOpaque) {
      this.navbarOpaque = isOpaque
    },

    ...mapActions(['logout', 'storeReturnRouteObject'])
  },

  mounted() {
    this.modalLoginTitle = this.$t('login')
    window.addEventListener('scroll', this.scrollHandler)
    window.addEventListener('resize', this.widthHandler)
    window.addEventListener('resize', this.widthHandler)
    window.addEventListener('storage', this.storageEvents)

    this.$root.$on('logout', this.doLogout)
    this.$root.$on('showLogin', this.showModalLogin)
    this.$root.$on('navbarOpaque', this.handleNavbarOpacity)
  },

  destroyed() {
    window.removeEventListener('scroll', this.scrollHandler)
    window.removeEventListener('resize', this.widthHandler)
    window.removeEventListener('storage', this.storageEvents)

    this.$root.$off('logout', this.doLogout)
    this.$root.$off('showLogin', this.showModalLogin)
    this.$root.$off('navbarOpaque', this.handleNavbarOpacity)
  }
}
</script>

<style lang="scss">
@import 'assets/sass/_variables';

.profile {
  .artist-search-container {
    display: none;
  }
}

.dropdown-backdrop,
.search-backdrop {
  position: fixed;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  min-height: 100%;
}

#dropdown-followed {
  max-height: 80vh;
  width: 425px !important;
  overflow: hidden;
  top: 48px;

  .wrapper {
    width: 450px !important;
    max-height: 80vh;
    overflow-y: scroll;
  }
}

.dropdown {
  color: white !important;
}

.isStatic {
  .dropdown {
    color: $navbar-dark-text !important;
  }
}

.is-link {
  cursor: pointer;
}

#mainnav {
  &.light-shaded {
    .dropdown {
      color: $navbar-dark-text !important;
    }
  }
}

.navbar-end {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .language-container {
    display: inline-flex;

    &.mobile {
      position: absolute;
      top: 11px;
      right: 45px;
    }

    .mobile-language-link {
      font-size: 1.2em;
      margin-right: 25px;
    }
  }
}

// Clean navbar follow button styling
.navbar-follow-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
}

.follow-navbar-button {
  margin-left: -2px;
  font-size: 0.9rem !important;
  padding: 1rem !important;
  margin: 0 !important;
}

// Fade transition animation
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.login-modal {
  margin: auto;
}

@media screen and (max-width: $mobile-portrait) {
  body.isProfile #mainnav .claim {
    color: #fb3e52;
  }

  body.isProfile #mainnav .burger span {
    background: #fb3e52 !important;
  }
}
</style>
